import { CheckIconSvg } from "components/icons/CheckIconSvg";
import { useTranslationContext } from "context/translation";
import { FC, useCallback } from "react";
import { StructuredText } from "react-datocms/structured-text";
import st from "./PasswordRequirements.module.scss";

export enum PasswordRequirementsMsgKeys {
  min8CharactersRequired = "min8CharactersRequired",
  oneLowercaseAlphaRequired = "oneLowercaseAlphaRequired",
  oneSpecialCharRequired = "oneSpecialCharRequired",
  oneUppercaseAlphaRequired = "oneUppercaseAlphaRequired",
  twoNumericDigitsRequired = "twoNumericDigitsRequired",
}

// copied from v2 schema, once in v3 schema import that instead
export interface PasswordRequirement {
  msg: string;
  satisfied: boolean;
}

interface PasswordRequirementsProps {
  hasEnteredPassword: boolean;
  passwordRequirements: PasswordRequirement[];
}

export const PasswordRequirements: FC<PasswordRequirementsProps> = ({
  passwordRequirements,
}) => {
  const {
    useAtLeast8Characters,
    useAtLeast1UppercaseAnd1LowercaseLetter,
    useAtLeast2Numbers,
    useAtLeast1SpecialCharacter,
  } = useTranslationContext();

  const Icon: FC<{ satisfied: boolean }> = ({ satisfied }) =>
    satisfied ? <CheckIconSvg className={st.successIcon} /> : null;

  const getRequirement = useCallback(
    (key: PasswordRequirementsMsgKeys) => {
      const req = passwordRequirements.find(({ msg }) => {
        const parsedMsg = msg as unknown as PasswordRequirementsMsgKeys;
        if (Object.values(PasswordRequirementsMsgKeys).includes(parsedMsg)) {
          return parsedMsg === key;
        }
        return null;
      });
      return req ? req.satisfied : false;
    },
    [passwordRequirements],
  );

  return (
    <div className={st.requirementsContainer}>
      <div className={st.requirementsItem}>
        <Icon
          satisfied={getRequirement(
            PasswordRequirementsMsgKeys.min8CharactersRequired,
          )}
        />
        <StructuredText data={useAtLeast8Characters} />
      </div>

      <div className={st.requirementsItem}>
        <Icon
          satisfied={
            getRequirement(
              PasswordRequirementsMsgKeys.oneUppercaseAlphaRequired,
            ) &&
            getRequirement(
              PasswordRequirementsMsgKeys.oneLowercaseAlphaRequired,
            )
          }
        />
        <StructuredText data={useAtLeast1UppercaseAnd1LowercaseLetter} />
      </div>

      <div className={st.requirementsItem}>
        <Icon
          satisfied={getRequirement(
            PasswordRequirementsMsgKeys.twoNumericDigitsRequired,
          )}
        />
        <StructuredText data={useAtLeast2Numbers} />
      </div>

      <div className={st.requirementsItem}>
        <Icon
          satisfied={getRequirement(
            PasswordRequirementsMsgKeys.oneSpecialCharRequired,
          )}
        />
        <StructuredText data={useAtLeast1SpecialCharacter} />
      </div>
    </div>
  );
};
